<template>
    <component
        class="form-button inline-flex cursor-pointer items-center justify-center rounded-md border font-medium leading-none shadow-sm transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-75"
        :class="[
            {
                'is-pressed': pressed,
                'has-content': hasContentInSlot,
            },
            variantClasses,
        ]"
        :disabled="disabled || loading"
        :href="href"
        :target="target"
        :is="tagName"
        :title="title"
        :type="buttonType"
        :to="to"
        @click="onClick"
    >
        <svg
            v-if="loading"
            class="h-3.5 w-3.5 animate-spin"
            :class="{ 'mr-2': hasContentInSlot }"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
            ></circle>
            <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
        </svg>
        <Icon
            v-if="icon && !loading"
            :icon="icon"
            class="h-4 w-4"
            :class="[{ 'mr-2': hasContentInSlot }]"
        />
        <slot />
    </component>
</template>

<script>
export const ButtonVariants = {
    PRIMARY: "primary",
    SECONDARY: "secondary",
    OUTLINE: "outline",
    WARNING: "warning",
    SMALL: "small",
};

import Icon from "@/components/icons/BaseIcon";
import { IconEnums } from "@/utils/icons";

export default {
    name: "FormButton",
    components: {
        Icon,
    },
    props: {
        href: {
            type: String,
            required: false,
        },
        target: {
            type: String,
            required: false,
        },
        to: {
            type: Object,
            required: false,
        },
        title: {
            type: String,
            required: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        pressed: {
            type: Boolean,
            default: false,
        },
        type: {
            type: String,
            default: "button",
        },
        loading: {
            type: Boolean,
            default: false,
        },
        variant: {
            type: String,
            default: ButtonVariants.PRIMARY,
            validator(type) {
                return Object.values(ButtonVariants).includes(type);
            },
        },
        icon: {
            type: Object,
            required: false,
            validator(IconEnum) {
                return Object.values(IconEnums).includes(IconEnum);
            },
        },
    },
    computed: {
        isLink() {
            return !!(this.href || this.to);
        },
        tagName() {
            if (this.to) {
                return "router-link";
            }
            if (this.href) {
                return "a";
            }
            return "button";
        },
        buttonType() {
            return this.isLink ? null : this.type;
        },
        hasContentInSlot() {
            return !!(this.$slots.default && this.$slots.default[0]);
        },
        variantClasses() {
            if (this.variant === ButtonVariants.SECONDARY) {
                return "border-slate-300 text-slate-800 bg-white hover:bg-slate-100 focus:ring-offset-slate-200 focus:ring-slate-400 px-4 py-2.5 text-sm";
            } else if (this.variant === ButtonVariants.OUTLINE) {
                return "border-slate-300 text-slate-700 bg-white hover:text-slate-800 focus:ring-offset-slate-100 focus:ring-slate-200 px-4 py-2.5 text-sm";
            } else if (this.variant === ButtonVariants.WARNING) {
                return "border-orange-500 text-white bg-orange-400 hover:border-orange-600 hover:bg-orange-500 focus:ring-offset-orange-100 focus:ring-orange-500 px-4 py-2.5 text-sm";
            } else if (this.variant === ButtonVariants.SMALL) {
                return "border-slate-300 text-slate-700 bg-white hover:bg-slate-100 hover:text-slate-800 focus:ring-offset-slate-100 focus:ring-slate-200 px-2 py-1.5 text-sm";
            }

            return "border-slate-600 text-white bg-slate-900 hover:bg-slate-800 focus:ring-offset-slate-100 focus:ring-slate-800 px-4 py-2.5 text-sm";
        },
    },
    methods: {
        onClick(event) {
            /**
             * Emitted when the button is clicked.
             * @event click
             * @type {Event}
             */
            this.$emit("click", event);
        },
    },
};
</script>
